import { FC, useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { FormType } from "../../common";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import DefinitionsDialog from "../../CommonDataParts/v2.0/DefinitionsDialog";
import lookupValues from "../../../../helpers/lookupValues";
import BeefHousingMMSComponent from "./BeefHousingMMSComponent";
import { BeefIntervention } from "../../../../models/Intervention/BeefIntervention";
import BeefHousingComponent from "./BeefHousingComponent";
import { CattleHousingAndManure } from "../../../../models/Intervention/CattleIntervention";
import EditModeButtons from "../../../helpers/EditModeButtons";
import { CattleHousingAndManure as CattleHousingAndManureBaseline } from "../../../../models/Baseline/CattleBaseline";

export interface DBeefHousingFormFormProps {
  formType: FormType;
  stageIndex: number;
  baseline: CattleHousingAndManureBaseline;
  currentFormsValues: CattleHousingAndManure;
  stageType: string;
  fieldItemPrefix: string;
  isMMSEdit: boolean;
  setIsMMSEdit: (val: boolean) => void;
}

const BeefHousingAndManureForm: FC<DBeefHousingFormFormProps> = ({
  formType = FormType.Add,
  baseline,
  currentFormsValues,
  stageIndex = 0,
  fieldItemPrefix,
  isMMSEdit,
  setIsMMSEdit,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const fc = useFormContext<BeefIntervention>();
  const [displayMMSRows, setDisplayMMSRows] = useState<boolean>(true);
  const [isDescriptionDialogVisible, setIsDescriptionDialogVisible] = useState(false);
  const [, forceUpdate] = useState({});

  const {
    fields: mmsAdditions,
    append: appendMMSAdditions,
    remove: removeMMSAdditions,
  } = useFieldArray({
    name: `${fieldItemPrefix}.manureSystemsAdditions`,
    control: fc.control,
    keyName: "keyId",
  });

  const timeInHousing = fc.watch(`${fieldItemPrefix}.timeInHousing`);

  useEffect(() => {
    if (timeInHousing === undefined || timeInHousing === "" || Number(timeInHousing) > 0)
      setDisplayMMSRows(true);
    else {
      setDisplayMMSRows(false);

      for (let index = mmsAdditions.length - 1; index >= 0; index--) {
        removeMMSAdditions(index);
      }
    }
  }, [timeInHousing]);

  const cancelMMSEdit = () => {
    fc.setValue(
      `${fieldItemPrefix}.timeInHousing`,
      currentFormsValues?.timeInHousing ?? ""
    );
    fc.setValue(
      `${fieldItemPrefix}.timeInFeedlot`,
      currentFormsValues?.timeInFeedlot ?? ""
    );
    fc.setValue(
      `${fieldItemPrefix}.timeGrazingLargeAreas`,
      currentFormsValues?.timeGrazingLargeAreas ?? ""
    );
    fc.setValue(
      `${fieldItemPrefix}.timeGrazingPastures`,
      currentFormsValues?.timeGrazingPastures ?? ""
    );
    fc.setValue(
      `${fieldItemPrefix}.timeInHousingTied`,
      currentFormsValues?.timeInHousingTied ?? ""
    );

    const manureSystems = baseline.manureSystems;
    manureSystems
      ?.filter((item) => !!item.mmsType)
      ?.forEach((_, index) => {
        const lastValue = currentFormsValues?.manureSystems?.[index];

        fc.setValue(
          `${fieldItemPrefix}.manureSystems.${index}.mmsType`,
          lastValue?.mmsType ?? manureSystems[index].mmsType
        );
        fc.setValue(
          `${fieldItemPrefix}.manureSystems.${index}.storagePeriod`,
          lastValue?.storagePeriod ?? manureSystems[index].storagePeriod
        );

        fc.setValue(
          `${fieldItemPrefix}.manureSystems.${index}.share`,
          lastValue?.share ?? manureSystems[index].share
        );
      });

    for (let index = mmsAdditions.length - 1; index >= 0; index--) {
      removeMMSAdditions(index);
    }

    const lastManureAdditions =
      currentFormsValues?.manureSystemsAdditions ?? [];
    lastManureAdditions.forEach((addition) => {
      appendMMSAdditions(addition);
    });

    setIsMMSEdit(false);
    forceUpdate({});
  };

  const closeDescriptionDialog = () => setIsDescriptionDialogVisible(false);

  return (
    <div style={{ paddingTop: 0 }}>
      {isDescriptionDialogVisible && (
        <DefinitionsDialog
          open
          prefix="BASELINE.FORM.INPUT.CATTLE.MMS"
          itemHeader="BASELINE.FORM.BARN.MANURE_MANAGEMENT_SYSTEM"
          items={lookupValues.cattleMMSDefinitions}
          handleClose={() => closeDescriptionDialog()}
        />
      )}

      <p
        className={classes.headingTitleVariant}
        style={{ marginTop: "var(--dsm-spacing-px-4)" }}
      >
        {intl.formatMessage({
          id: "SUSTELL.STAGE.BEEF.MMS.TIME_SPENT_IN_HOUSING.DESCRIPTION",
        })}
      </p>
      <DsmGrid
        className={
          !isMMSEdit
            ? classes.dsmGridTwoColumn2nd45NoGap
            : classes.dsmGridTwoColumn2nd70NoGap
        }
      >
        <DsmGrid
          className={classes.dsmGridOneColumnWithBorder}
          style={{ padding: 0, gap: 0 }}
        >
          <BeefHousingComponent
            isViewMode={formType === FormType.View || !isMMSEdit}
            formType={formType}
            baseline={baseline}
            fieldItemPrefix={fieldItemPrefix}
          />

          <div
            style={{
              padding: "var(--dsm-spacing-px-4)",
              paddingTop: 0,
              width: "160px",
            }}
          >
            {formType !== FormType.View &&
              <EditModeButtons
                isEditing={isMMSEdit}
                onEdit={() => setIsMMSEdit(true)}
                onCancel={cancelMMSEdit}
              />}
          </div>
        </DsmGrid>

        <div className={classes.dsmGridOneColumnLightBlueBoxContainer}>
          <BeefHousingMMSComponent
            formType={formType}
            isViewMode={formType === FormType.View || !isMMSEdit}
            fieldItemPrefix={fieldItemPrefix}
            baseline={baseline}
            stageIndex={stageIndex}
            displayMMSRows={displayMMSRows}
            mmsCollection={{
              mmsAdditions: mmsAdditions,
              appendMMSAdditions: appendMMSAdditions,
              removeMMSAdditions: removeMMSAdditions,
            }}
            openDescriptionDialog={() => setIsDescriptionDialogVisible(true)}
          />
        </div>
      </DsmGrid>
    </div>
  );
};

export default BeefHousingAndManureForm;
